/**
 * Sending: the client gift-wraps the message and the relay carries it.
 *
 * This is the write half of the Go/TypeScript seam. `seal.ts` opens what Go
 * sealed; this file seals what Go opens, and `internal/outbox` is the reader.
 * The fixtures in `testdata/vectors/outbox.json` are written here and read by
 * `internal/outbox/vectors_test.go`, so never change this file without running
 * both sides.
 *
 * Nothing public writes to the machine that holds the mail. postrd reads the
 * relay every 15 seconds, opens the wrap, checks that the sealing key holds the
 * From address, and sends.
 *
 * The cost with a phone signer is one encrypt and one signature — the NIP-59
 * minimum, because the seal must be signed by the account's own key. The wrap
 * around it is signed by a throwaway key made here, which costs nothing.
 */
import {
  finalizeEvent, generateSecretKey, getEventHash, nip44,
  type Event as NostrEvent,
} from "nostr-tools";
import type { BlobRef } from "./seal";
import type { MailSigner } from "./signer";
import type { RelayConnection } from "./relay";

/** Rumor kind carrying a message to send. Must match outbox.KindOutbox in Go. */
export const KIND_OUTBOX = 1315;
/** NIP-59: the seal, and the wrap around it. */
export const KIND_SEAL = 13;
export const KIND_GIFT_WRAP = 1059;

/** What the client writes inside the rumor. Field names match the Go struct. */
export interface OutboxRequest {
  from: string;
  to: string[];
  cc?: string[];
  subject: string;
  text?: string;
  html?: string;
  in_reply_to?: string;
  references?: string[];
  /**
   * Files already in the blob store, named rather than carried: a forward
   * refers to what arrived on the message it forwards, and a rumor has about
   * 40KB for everything.
   */
  attachments?: BlobRef[];
}

/**
 * NIP-59 back-dates both layers by up to six hours, so an observer cannot read
 * the traffic off the timestamps. postrd's filter allows twelve.
 */
function backdated(): number {
  return Math.floor(Date.now() / 1000) - Math.floor(Math.random() * 6 * 60 * 60);
}

/**
 * Wrap one request for the ingest identity.
 *
 * Two layers with two different counterparties: the rumor is encrypted to
 * ingest under the account's own key, and the seal under a throwaway key. Using
 * one key for both is the mistake that looks like it works — the outer layer
 * opens and the inner one fails with an opaque hmac error.
 */
export async function wrapOutbox(
  request: OutboxRequest,
  signer: MailSigner,
  ingestPubkey: string,
): Promise<NostrEvent> {
  const rumor = {
    kind: KIND_OUTBOX,
    pubkey: signer.pubkey,
    created_at: Math.floor(Date.now() / 1000),
    tags: [] as string[][],
    content: JSON.stringify(request),
  };
  // A rumor is never signed. It carries an id so it is a complete event, and
  // the reader recomputes that id from the seal's pubkey rather than trusting
  // what is written here.
  const sealed = { ...rumor, id: getEventHash(rumor), sig: "" };

  const sealContent = await signer.nip44Encrypt(ingestPubkey, JSON.stringify(sealed));
  const seal = await signer.signEvent({
    kind: KIND_SEAL,
    created_at: backdated(),
    tags: [],
    content: sealContent,
  });

  const nonceKey = generateSecretKey();
  const conversation = nip44.v2.utils.getConversationKey(nonceKey, ingestPubkey);
  return finalizeEvent(
    {
      kind: KIND_GIFT_WRAP,
      created_at: backdated(),
      tags: [["p", ingestPubkey]],
      content: nip44.v2.encrypt(JSON.stringify(seal), conversation),
    },
    nonceKey,
  );
}

/**
 * Publish one message.
 *
 * It signs once and publishes once. There is no retry: a second attempt is a
 * second approval on the phone, and a wrap that reached the relay before the
 * error would then be sent twice. A failure is reported so the person can press
 * Send again, which is a decision they get to make.
 */
export async function sendOutbox(
  relay: RelayConnection,
  request: OutboxRequest,
  signer: MailSigner,
  ingestPubkey: string,
): Promise<NostrEvent> {
  const wrap = await wrapOutbox(request, signer, ingestPubkey);
  await relay.publish(wrap);
  return wrap;
}
